import { useMemo } from 'react';

import {
  useFeatureFlag,
  useLayerParamExposure,
  useLayerParamLoading,
  useLayerParamValue,
} from '@growtherapy/analytics/client';

import { ExperimentLayers } from '../statsig/experiments';
import { providerUtilizationTelehealthPspGate } from '../statsig/gates';

const [, { providerUtilizationVariant }] = ExperimentLayers.ProviderUtilization;

type ProviderUtilizationExperiment = {
  includeLiveMeasures: boolean;
  exposureRef: ReturnType<typeof useLayerParamExposure>['ref'] | undefined;
  isLoading: boolean;
};

/**
 * Resolves the shared provider-level experiment assignment and Telehealth rollback gate.
 *
 * Consumers wait for `isLoading` before mounting the PSP, so its first query uses the
 * final allocation and gate values. When the gate is enabled, attaching `exposureRef`
 * records either a control or treatment exposure; disabling the gate suppresses both
 * treatment live measures and experiment exposure without rebucketing the provider.
 */
export function useProviderUtilizationExperiment(): ProviderUtilizationExperiment {
  const allocationValue = useLayerParamValue<string>(providerUtilizationVariant);
  const isAllocationLoading = useLayerParamLoading(providerUtilizationVariant);
  const { ref } = useLayerParamExposure(providerUtilizationVariant);
  const { hasFeature: isTelehealthPspEnabled, isLoading: isTelehealthPspGateLoading } = useFeatureFlag(
    providerUtilizationTelehealthPspGate,
  );

  return useMemo(() => {
    const allocation = allocationValue === 'treatment' ? 'treatment' : 'control';
    const isTelehealthTreatmentEnabled = !isTelehealthPspGateLoading && isTelehealthPspEnabled;

    return {
      includeLiveMeasures: isTelehealthTreatmentEnabled && allocation === 'treatment',
      exposureRef: isTelehealthTreatmentEnabled ? ref : undefined,
      isLoading: isAllocationLoading || isTelehealthPspGateLoading,
    };
  }, [allocationValue, isAllocationLoading, isTelehealthPspEnabled, isTelehealthPspGateLoading, ref]);
}
